import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { dealerLogout, getDealerMe } from "../api/dealerAuthApi";
import { startPartnerDealerPortalSession } from "../services/automapPartnerDealerApi";
import {
  clearPartnerDealerPortalTarget,
  readPartnerDealerPortalTarget,
} from "../constants/automapPartnerDealerPortal";

const DealerAuthContext = createContext(null);

async function loadDealerSession() {
  const targetDealerId = readPartnerDealerPortalTarget();

  try {
    const body = await getDealerMe(targetDealerId);
    if (body.dealer?.dealerUserId) {
      return body.dealer;
    }
  } catch {
    // fall through to partner bootstrap
  }

  if (!targetDealerId) {
    return null;
  }

  await startPartnerDealerPortalSession(targetDealerId);
  const body = await getDealerMe(targetDealerId);
  return body.dealer || null;
}

export function DealerAuthProvider({ children }) {
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const sessionDealer = await loadDealerSession();
      setDealer(sessionDealer);
    } catch {
      setDealer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    const returnUrl = dealer?.adminReturnUrl || dealer?.partnerPortalReturnUrl;
    await dealerLogout().catch(() => {});
    clearPartnerDealerPortalTarget();
    setDealer(null);
    if (returnUrl) {
      window.location.assign(returnUrl);
    }
  }, [dealer?.adminReturnUrl, dealer?.partnerPortalReturnUrl]);

  const value = useMemo(
    () => ({
      dealer,
      loading,
      isAuthenticated: Boolean(dealer?.dealerUserId),
      refresh,
      logout,
    }),
    [dealer, loading, refresh, logout]
  );

  return <DealerAuthContext.Provider value={value}>{children}</DealerAuthContext.Provider>;
}

export function useDealerAuth() {
  const ctx = useContext(DealerAuthContext);
  if (!ctx) {
    throw new Error("useDealerAuth must be used within DealerAuthProvider");
  }
  return ctx;
}
